<template>
  <Popup :ref="(el) => popup = el"
         class="popup__lg"
         :title="$t('labels.trade_deal')"
         @onClose="hide"
  >
    <LoaderContent ref="loader" class="">

      <div class="popup__content">
        <div class="deal-tab" v-if="deal">
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ $t('labels.id') }}</div>
            <div class="deal-tab__info">
              <div class="d-flex align-items-center gap-xs">
                {{ deal.publicId }} <copy-button :value="deal?.publicId"></copy-button>
              </div>
            </div>
          </div>
          <div class="deal-tab__row" v-if="deal.transactionId">
            <div class="deal-tab__label text-capitalize-first">{{ $t('labels.transaction') }}</div>
            <div class="deal-tab__info">
              <div class="d-flex align-items-center gap-xs">
                {{ deal.transactionId }} <copy-button :value="deal.transactionId"></copy-button>
              </div>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ $t('labels.type') }}</div>
            <div class="deal-tab__info">{{ $t(`direction.${deal.type}`) }}</div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ $t('labels.date') }}</div>
            <div class="deal-tab__info">{{ dateHelper.format(deal.createdAt) }}</div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.status') }}</div>
            <div class="deal-tab__info">
              {{ helper.trans(`deal_status.${deal.status}`) }}
              <template v-for="attachment in deal.attachments" :key="attachment.id">
                <a :href="`${api.baseUrl()}/attachments/${attachment.file}`" target="_blank">
                  <SvgIcon class="icon icon__warning icon__small" icon="paperclip"></SvgIcon>
                </a>
                <ReceiptCheckControl :deal="deal" :attachment="attachment"></ReceiptCheckControl>
              </template>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ rateHeader }}</div>
            <div class="deal-tab__info">
              <span>{{ numberFormatter.formatNumber(deal.takerSum.amount / deal.makerSum.amount, deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
              <span v-if="deal.merchantMakerSum"> / {{ numberFormatter.formatNumber(deal.takerSum.amount / deal.merchantMakerSum.amount, deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
              <template v-if="isEffectiveRateEnabled && deal.rewardSum">
                <span> / {{ numberFormatter.formatNumber((deal.takerSum.amount / deal.makerSum.amount) * (1 + deal.rewardSum.amount/deal.makerSum.amount), deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
              </template>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.in_sum') }}</div>
            <div class="deal-tab__info">
              <div>
                <span v-if="deal.actualTakerSum">
                  {{ numberFormatter.formatMoney(deal.actualTakerSum) }}
                </span>
                <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                  {{ numberFormatter.formatMoney(deal.takerSum) }}
                </span>
                <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1">/</span>
                <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1 small text-secondary">
                  <span v-if="deal.actualMakerSum">
                    {{ numberFormatter.formatMoney(deal.actualMakerSum) }}
                  </span>
                  <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                    {{ numberFormatter.formatMoney(deal.makerSum) }}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.out_sum') }}</div>
            <div class="deal-tab__info">
              <div>
                <span v-if="deal.actualMakerSum">
                  {{deal.type === Direction.PAY_IN ? '-' : '+'}}{{ numberFormatter.formatMoney(deal.actualMakerSum) }}
                </span>
                <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                  {{deal.type === Direction.PAY_IN ? '-' : '+'}}{{ numberFormatter.formatMoney(deal.makerSum) }}
                </span>
                <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1">/</span>
                <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1 small text-secondary">
                  <span v-if="deal.actualTakerSum">
                    {{ numberFormatter.formatMoney(deal.actualTakerSum) }}
                  </span>
                  <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                    {{ numberFormatter.formatMoney(deal.takerSum) }}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.reward_sum') }}</div>
            <div class="deal-tab__info">
              <div v-if="deal.actualRewardSum">
                {{ numberFormatter.formatMoney(deal.actualRewardSum) }}
              </div>
              <div v-if="deal.rewardSum && (!deal.actualRewardSum || deal.isActualSumsChanged)" :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                {{ numberFormatter.formatMoney(deal.rewardSum) }}
              </div>
            </div>
          </div>
          <div class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.buyer') }}</div>
            <div class="deal-tab__info">{{ deal.takerId ?? 'n/a' }}</div>
          </div>
          <div class="deal-tab__row" :class="{'deal-tab__row--last': deal.paymentOption !== PaymentOption.MANUAL_ECOM && !showManualSbpQrAttachRow}">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.requisites') }}</div>
            <div class="deal-tab__info">
              <div class="download-documents__card-info card-info">
                <div class="card-info__title">
                  {{ helper.trans('payment_options.' + deal.paymentOption) }}
                </div>
                <div class="card-info__number">
                  <PayMethodIcon :method="deal.paymentMethod"></PayMethodIcon>
                  <PayoutQrBlock
                    v-if="deal.type === Direction.PAY_OUT && deal.payoutQr?.link"
                    :link="deal.payoutQr.link"
                  />
                  <VietQrPaymentBlock
                    v-else-if="helper.isEmvQrOption(deal.paymentOption)"
                    :deal-id="deal.id"
                    :qr-code-data="deal.qrCodeData ?? null"
                    :qr-code-link="helper.emvQrData(deal)"
                    :fallback-requisites="deal.paymentRequisites.requisites || ''"
                  />
                  <VietQrPaymentBlock
                    v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal)"
                    :deal-id="deal.id"
                    :qr-code-data="null"
                    :qr-code-link="helper.dealQrCodeLink(deal)"
                    :fallback-requisites="''"
                  />
                  <span v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR" class="text-secondary">—</span>
                  <template v-else>
                  <a v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <a v-else-if="deal.paymentOption === PaymentOption.CLICK_UZ_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.trans('labels.my_click_uz') }}</a>
                  <a v-else-if="deal.paymentOption === PaymentOption.TIPS && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <a v-else-if="deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <span v-else-if="deal.paymentOption === PaymentOption.MANUAL_ECOM || deal.paymentOption === PaymentOption.TO_CARD">{{ formatCardRequisites(deal.paymentRequisites.requisites) }}</span>
                  <span v-else>{{ deal.paymentRequisites.requisites }}</span>
                  </template>
                </div>
                <div class="card-info__name">
                  <div v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.requisites && !deal.requisites.requisites.startsWith('http')">
                    {{ deal.requisites.requisites }}
                  </div>
                  <div v-else>
                    {{ deal.paymentRequisites.holder }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="deal.paymentOption === PaymentOption.MANUAL_ECOM && deal.manualEcom3dsCode" class="deal-tab__row">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.manual_ecom_3ds_code') }}</div>
            <div class="deal-tab__info">{{ deal.manualEcom3dsCode }}</div>
          </div>
          <div v-if="deal.paymentOption === PaymentOption.MANUAL_ECOM" class="deal-tab__row deal-tab__row--last">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.client_card') }}</div>
            <div class="deal-tab__info">
              <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="showClientCard">
                {{ helper.trans('labels.show_client_card') }}
              </button>
            </div>
          </div>
          <div v-if="showManualSbpQrAttachRow" class="deal-tab__row deal-tab__row--last">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('labels.manual_sbp_qr_deal_image') }}</div>
            <div class="deal-tab__info">
              <ManualSbpQrLinkEditor :deal="deal" @applied="onManualSbpQrApplied" />
            </div>
          </div>
          <div v-if="deal.isVideoRequestAvailable" class="deal-tab__row deal-tab__row--last">
            <div class="deal-tab__label text-capitalize-first">{{ helper.trans('button.request_video') }}</div>
            <div class="deal-tab__info">
              <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" :disabled="isVideoRequested(deal)" :title="isVideoRequested(deal) ? dateHelper.format(deal.videoRequestedUntil) : null" @click="requestVideo">
                {{ isVideoRequested(deal) ? helper.trans('button.video_requested') : helper.trans('button.request_video') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="deal && deal.externalNspkTransaction">
        <div class="popup__title mb mt">
          <span class="text-capitalize-first">{{ $t('labels.external_transaction')}}</span>
        </div>
        <div class="popup__content">
          <div class="deal-tab">

            <div class="deal-tab__row">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.tx_id') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ deal.externalNspkTransaction.txId }} <copy-button :value="deal.externalNspkTransaction.txId"></copy-button>
                </div>
              </div>
            </div>

            <div class="deal-tab__row" v-if="deal.externalNspkTransaction.data.txNum">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.tx_num') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ deal.externalNspkTransaction.data.txNum }} <copy-button :value="deal.externalNspkTransaction.data.txNum"></copy-button>
                </div>
              </div>
            </div>

            <div class="deal-tab__row" v-if="deal.externalNspkTransaction.data.paymentSystemTransferNum">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.paymentSystemTransferNum') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ deal.externalNspkTransaction.data.paymentSystemTransferNum }} <copy-button :value="deal.externalNspkTransaction.data.paymentSystemTransferNum"></copy-button>
                </div>
              </div>
            </div>

            <div class="deal-tab__row" v-if="deal.externalNspkTransaction.data.sender">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.sender') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  <ExternalNspkSender :value="deal.externalNspkTransaction.data.sender" />
                </div>
              </div>
            </div>

            <div class="deal-tab__row">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.service') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ deal.externalNspkTransaction.service }}
                </div>
              </div>
            </div>

            <div class="deal-tab__row">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.status') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ deal.externalNspkTransaction.status }}
                </div>
              </div>
            </div>

            <div class="deal-tab__row">
              <div class="deal-tab__label text-capitalize-first">{{ $t('labels.date') }}</div>
              <div class="deal-tab__info">
                <div class="d-flex align-items-center gap-xs">
                  {{ dateHelper.format(deal.externalNspkTransaction.updatedAt) }}
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>




      <div class="popup__title mb mt">
        <span class="text-capitalize-first">{{ $t('labels.history')}}</span>
      </div>

      <div class="popup__content">
        <div style="overflow: auto">
          <table class="table table-striped" style="position: relative">
            <thead>
            <tr>
              <th class="text-start p-sm">{{ helper.trans('labels.date')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.status')}}</th>
              <th class="text-start p-sm" colspan="2">{{ helper.trans('labels.initiator')}}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="history in dealHistoriesResponse.items" :key="history.id">
              <td class="p-sm">{{ dateHelper.format(history.date) }}</td>
              <td class="p-sm">
                <div class="d-flex flex-column">
                  <div>
                    <span v-if="history.action">{{ helper.trans(`deal_history_action.${history.action}`) }}</span>
                    <span v-else>{{ helper.trans(`deal_status.${history.status}`) }}</span>
                    <a v-if="!history.action && history.disputeAttachment" :href="`${api.baseUrl()}/attachments/${history.disputeAttachment}`" target="_blank">
                      <SvgIcon class="icon icon__warning icon__small" icon="paperclip"></SvgIcon>
                    </a>
                  </div>
                  <small v-if="history.action === 'AMOUNT_CHANGED' && history.previousAmount && history.newAmount" class="text-muted">
                {{ history.previousAmount }} -> {{ history.newAmount }} {{ history.amountCurrency || '' }}
              </small>
              <small v-if="history.action === 'DISPUTE_AUTOCLOSE_PAUSED' && history.suspendDisputeAutocloseAt" class="text-muted">
                    {{ helper.trans('labels.pause_until', {date: dateHelper.format(history.suspendDisputeAutocloseAt), comment: history.suspendDisputeComment || ''}) }}
                  </small>

                  <span v-if="!history.action && (history.disputeReason === DisputeReason.INVALID_SUM || history.disputeReason === DisputeReason.PART_PAYOUT)" class="text-danger">
                        (<span class="text-lowercase">{{ helper.trans(`dispute_reason.${history.disputeReason}`) }} </span>: {{numberFormatter.formatNumber(history.disputeReasonData.amount, 2)}} {{deal.takerSum.currency}})
                        </span>
                  <ReceiptCheckStatus v-else-if="!history.action && isPayOutReceiptCheck(deal.type, history.disputeReason)" :reason="history.disputeReason" :data="history.disputeReasonData"></ReceiptCheckStatus>
                  <span v-else-if="!history.action && history.disputeReason" class="text-lowercase text-danger">
                        ({{ helper.trans(`dispute_reason.${history.disputeReason}`)}})
                        </span>
                </div>

              </td>
              <td class="p-sm">
                <small v-if="history.initiatorRole" class="text-capitalize-first d-inline-block">{{ helper.trans(`user_roles.${history.initiatorRole}`) }}</small>
                <small v-else>{{ helper.trans(`initiator_type.${history.initiatorType}`) }}</small>
              </td>
            </tr>
            </tbody>
          </table>

          <b-pagination
            v-if="dealHistoriesResponse.totalCount > dealHistoriesResponse.items.length"
            v-model="historyPage"
            :total-rows="dealHistoriesResponse.totalCount"
            :per-page="dealHistoriesResponse.limit"
            :limit="20"
            @update:modelValue="loadHistory()"
            prev-text="Back"
            next-text="Next"
            align="start"
            first-number
            hide-goto-end-buttons
            hide-ellipsis
          ></b-pagination>
        </div>
      </div>

    </LoaderContent>
    <Popup
      :ref="(el) => clientCardPopup = el"
      :title="helper.trans('labels.client_card')"
      @onClose="hideClientCard"
    >
      <div class="popup__content" v-if="clientCardData">
        <div class="form-group mb-3">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.card_number') }}</label>
          <input
            type="text"
            class="form-control"
            :value="formatCardRequisites(clientCardData.cardNumber)"
            readonly
          >
        </div>
        <div class="form-group mb-3">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.expiry') }}</label>
          <input
            type="text"
            class="form-control"
            :value="`${clientCardData.expiryMonth}/${clientCardData.expiryYear}`"
            readonly
          >
        </div>
        <div class="form-group mb-3">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.cardholder') }}</label>
          <input
            type="text"
            class="form-control"
            :value="clientCardData.cardholderName"
            readonly
          >
        </div>
        <div class="form-group mb-3">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.cvv') }}</label>
          <input
            type="text"
            class="form-control"
            :value="clientCardData.cvv"
            readonly
          >
        </div>
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="btn btn-outline-dark btn-sm text-capitalize-first" @click="hideClientCard">
            {{ helper.trans('button.ok') }}
          </button>
        </div>
      </div>
    </Popup>
    <ConfirmPopup ref="confirmPopup"></ConfirmPopup>
  </Popup>
</template>
<script lang="ts">
import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import Popup from "@/apps/public/components/Popup.vue";
import axios from "axios";
import {$t, helper} from "@/service/CommonHelper";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import { useStore } from 'vuex';
import { api } from '@/service/APIClient';
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import {BPagination} from "bootstrap-vue-3";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import DealHistoryDTO from "@/models/DealHistoryDTO";
import PaginationDTO from "@/models/PaginationDTO";
import {InitiatorType} from "@/models/enum/InitiatorType";
import {Dictionary} from "@/types";
import UserDTO from "@/models/UserDTO";
import TraderDealDTO from "@/models/TraderDealDTO";
import {dateHelper} from "@/service/DateHelper";
import {DisputeReason, isPayOutReceiptCheck} from "@/models/enum/DisputeReason";
import ReceiptCheckStatus from "@/components/ReceiptCheckStatus.vue";
import {DealStatus} from "@/models/enum/DealStatus";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import {Direction} from "@/models/enum/Direction";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";
import SvgIcon from "@/apps/public/components/SvgIcon.vue";
import ReceiptCheckControl from "@/apps/public/components/ReceiptCheckControl.vue";
import ClientCardDTO from "@/models/ClientCardDTO";
import {useNotification} from "@kyvg/vue3-notification";
import {PaymentMethod} from "@/models/enum/PaymentMethod";
import VietQrPaymentBlock from "@/components/VietQrPaymentBlock.vue";
import PayoutQrBlock from "@/apps/public/components/PayoutQrBlock.vue";
import ManualSbpQrLinkEditor from "@/apps/public/components/ManualSbpQrLinkEditor.vue";
import ExternalNspkSender from "@/components/ExternalNspkSender.vue";
import ConfirmPopup from "@/apps/public/components/ConfirmPopup.vue";


export default defineComponent({
  methods: {$t},
  components: {
    ReceiptCheckStatus,
    ConfirmPopup,
    ExternalNspkSender,
    ManualSbpQrLinkEditor, VietQrPaymentBlock, PayoutQrBlock, SvgIcon, ReceiptCheckControl, PayMethodIcon, CopyButton, BPagination, LoaderContent, LoaderButton, FormInputWrapper, Popup},
  setup() {
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const confirmPopup = ref<InstanceType<typeof ConfirmPopup> | null>(null)
    const clientCardPopup = ref<InstanceType<typeof Popup> | null>(null)
    const clientCardData = ref<ClientCardDTO | null>(null)
    const store = useStore()
    const notification = useNotification()
    const isEffectiveRateEnabled = computed(() => (store.getters.getUser?.isEffectiveRateEnabled ?? false) && (store.getters.isTrader || store.getters.isTraderOperator))

    const formatCardRequisites = (requisites: string) => {
      return requisites.replace(/(\d{4})/g, '$1 ').trim();
    };

    const hideClientCard = () => {
      clientCardData.value = null;
      clientCardPopup.value?.hide();
    };

    const clearClientCardData = () => {
      clientCardData.value = null;
    };

    const showClientCard = async () => {
      if (!state.deal) return;
      try {
        clientCardData.value = await api.dealClientCard(state.deal.id);
        clientCardPopup.value?.show();
      } catch (e) {
        notification.notify({ type: 'error', title: helper.trans('server_errors.INTERNAL_ERROR') });
      }
    };

    const state = reactive({
      deal: null as TraderDealDTO|null,
      historyPage: 1,
      dealHistoriesResponse: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<DealHistoryDTO>
    });
    const manualSbpQrAttachStatuses = [DealStatus.TRANSFER_WAITING]

    const onManualSbpQrApplied = (updated: TraderDealDTO) => {
      if (state.deal) {
        Object.assign(state.deal, updated)
      }
    }

    const isVideoRequested = (deal: TraderDealDTO): boolean => {
      return deal.videoRequestedUntil != null && new Date(deal.videoRequestedUntil) > new Date()
    }

    const requestVideo = () => {
      const deal = state.deal
      if (!deal) {
        return
      }
      confirmPopup.value?.show({
        title: $t('labels.deal_action_confirmation', {action: $t('button.request_video')}),
        okTitle: $t('button.request_video'),
        onOkClick: async () => {
          try {
            const updated = await api.call.traderDeals.requestVideo(deal.id);
            notification.notify({type: 'success', title: $t('success.video_request_sent')});
            Object.assign(deal, updated)
          } catch (e) {
            if (helper.isValidationErrors(e)) {
              for (const item of helper.getValidationErrors(e)) {
                notification.notify({type: 'error', title: $t(`server_errors.${item.code}`, {}, item.message)});
              }
              Object.assign(deal, await api.deal(deal.id))
            }
          }
        }
      })
    }

    const showManualSbpQrAttachRow = computed(() => {
      const d = state.deal
      if (!d) {
        return false
      }
      if (d.paymentOption !== PaymentOption.MANUAL_SBP_QR) {
        return false
      }
      if (d.type !== Direction.PAY_IN) {
        return false
      }
      if (!(store.getters.isTrader || store.getters.isTraderOperator || store.getters.isTeamLead)) {
        return false
      }
      return manualSbpQrAttachStatuses.includes(d.status as DealStatus)
    })

    const rateHeader = computed(() => {
      if (state.deal?.merchantMakerSum) {
        return helper.trans('labels.trader_rate_merchant_rate')
      }
      const base = helper.trans('labels.rate')
      if (!isEffectiveRateEnabled.value) {
        return base
      }
      return `${base} / ${helper.trans('labels.effective_rate_short')}`
    })


    const loadHistory = async () => {
      if (!state.deal) {
        return;
      }

      state.dealHistoriesResponse.offset = state.dealHistoriesResponse.limit * (state.historyPage - 1)
      state.dealHistoriesResponse = await api.dealHistory(state.deal.id, state.dealHistoriesResponse);
      if (!(store.getters.isTrader || store.getters.isTraderOperator)) {
        let usersIds = [] as string[];
        for (const item of state.dealHistoriesResponse.items) {
          if (item.traderId && !usersIds.includes(item.traderId.toString())) {
            usersIds.push(item.traderId.toString())
          }

          if (item.initiatorType === InitiatorType.USER && !usersIds.includes(item.initiatorId)) {
            usersIds.push(item.initiatorId)
          }
        }

        const users = (await api.users({limit: usersIds.length, offset:0 } as PaginationDTO, {ids: usersIds})).items;
        let userByIds = {} as Dictionary<UserDTO>
        for (const user of users) {
          userByIds[user.id] = user
        }

        for (const item of state.dealHistoriesResponse.items) {
          if (item.traderId) {
            item.trader = userByIds[item.traderId] ?? null;
          }

          if (item.initiatorType === InitiatorType.USER) {
            item.initiator = userByIds[parseInt(item.initiatorId)] ?? null
          }
        }
      }

    }

    const show = async (deal: TraderDealDTO|string) => {
      popup.value?.show()
      loader.value?.load(async () => {
        if (typeof deal === 'object') {
          state.deal = deal
        } else {
          state.deal = await api.deal(deal)
        }

        await loadHistory()
      })
    }

    const hide = () => {
      clearClientCardData();
      clientCardPopup.value?.hide();
      popup.value?.hide();
    }

    return {
      ...toRefs(state),
      loader,
      popup,
      helper,
      show,
      dateHelper,
      DisputeReason,
      isPayOutReceiptCheck,
      loadHistory,
      numberFormatter,
      api,
      PaymentOption,
      PaymentMethod,
      Direction,
      hide,
      clientCardData,
      clientCardPopup,
      formatCardRequisites,
      showClientCard,
      hideClientCard,
      clearClientCardData,
      isEffectiveRateEnabled,
      rateHeader,
      onManualSbpQrApplied,
      showManualSbpQrAttachRow,
      confirmPopup,
      isVideoRequested,
      requestVideo,
    }
  }
})
</script>
